import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useNotification } from 'config/Notification';
import { utils, write } from 'xlsx';
import LoadingOverlay from 'components/LoadingOverlay.jsx';

import { fetchAssessmentReportdetailsforReview as fetchAssessmentReportdetailsforReviewService } from 'services/modules/cap/standardservice';
import { putAssessmentStatus as putAssessmentStatusService } from 'services/modules/cap/assessmentservice';

import '../../styles/cap.css';

import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import CreditScore from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

import {
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import QuestionRenderer from '../../components/shared/QuestionRenderer';

// Status helper — whether Mitigation Response / evidence columns should show
const showResponseColumns = (status) =>
  ['Mitigation Response Submitted', 'Review Mitigation Response',
    'Mitigation Response In Progress', 'Completed'].includes(status);

const ReviewMitigationPage = () => {

  const location = useLocation();
  const urlparams = location.state;
  const navigate = useNavigate();
  const { openNotification } = useNotification();

  const [assessmentsList, setAssessmentsList] = useState([]);
  const [confirmReject, setConfirmReject] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [isApprove, setIsApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mitigationRejection, setMitigationRejection] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ── Details dialog state ──────────────────────────────────────────────────
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedSubcontrol, setSelectedSubcontrol] = useState(null);

  const openDetailDialog = (subcontrol) => {
    setSelectedSubcontrol(subcontrol);
    setDetailDialog(true);
  };

  const closeDetailDialog = () => {
    setDetailDialog(false);
    setSelectedSubcontrol(null);
  };

  let body = { assessment_id: urlparams.assessmentid };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleRowClick = (rowId) => {
    setExpandedRow((prevRow) => (prevRow === rowId ? null : rowId));
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchPendingMitigation = useCallback(async () => {
    setLoading(true);
    try {
      let params = { assessment_id: urlparams.assessmentid };
      const response = await fetchAssessmentReportdetailsforReviewService({ params });
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.controls || [];

      setAssessmentsList(data);
      data.forEach((e) => {
        if (e.mitigation_rejection === 1) setMitigationRejection(true);
      });
    } catch (error) {
      openNotification("Error fetching pending assessments:", 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reviewMitigation = useCallback(async () => {
    try {
      body.status = "Review Mitigation Response";
      const response = await putAssessmentStatusService({ body });
      if (response.data !== '') {
        urlparams.status = "Review Mitigation Response";
        await fetchPendingMitigation();
      }
    } catch (error) {
      openNotification('Error', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (urlparams.status === 'Mitigation Response Submitted') {
      reviewMitigation();
    } else {
      fetchPendingMitigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Approve / Reject ──────────────────────────────────────────────────────
  const handleApprove = async () => {
    try {
      body.status = "Completed";
      let response = await putAssessmentStatusService({ body });
      if (response.data !== '') {
        navigate('/cap/mitigationsuccess', {
          state: { assessmentid: urlparams.assessmentid, standard: urlparams.standard, status: 'Completed' }
        });
      }
    } catch (error) {
      openNotification('Error while approving', 'error');
    }
  };

  const handleReject = async () => {
    try {
      navigate('/cap/rejectmitigation', {
        state: { assessmentid: urlparams.assessmentid, standard: urlparams.standard, status: urlparams.status }
      });
    } catch (error) {
      openNotification('Error while rejecting', 'error');
    }
  };

  const goBack = () => {
    if (urlparams.status === 'Completed') {
      navigate('/cap/assessments-history');
    } else {
      navigate('/cap/mitigationplan');
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const exportToExcel = () => {
    let index = 0;
    const workbook = utils.book_new();
    const worksheet = utils.aoa_to_sheet([['Control Name']]);
    let allMergeCellRange = [];
    allMergeCellRange.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } });

    assessmentsList.forEach((item) => {
      index += 1;
      utils.sheet_add_aoa(worksheet, [[item.controlName]], { origin: index });
      allMergeCellRange.push({ s: { r: index, c: 0 }, e: { r: index, c: 6 } });
      index += 1;
      utils.sheet_add_aoa(worksheet, [['Ref', 'Control', 'Rating', 'Rationale for Rating', 'Mitigation Suggested', 'Mitigation Response', 'Evidence']], { origin: index });
      item.subcontrols.forEach((ele) => {
        index += 1;
        utils.sheet_add_aoa(worksheet, [[
          ele.subcontrol, ele.subcontrolName, ele.severity, ele.rational, ele.mitigation,
          ele.mitigationresponse?.remarks || '',
          ele.mitigationresponse?.evidence || ''
        ]], { origin: index });
      });
    });

    worksheet['!merges'] = allMergeCellRange;
    utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = write(workbook, { type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Mitigation' + urlparams.assessmentid + '.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const showResponse = showResponseColumns(urlparams.status);

  return (
    <div>
      {/* ── Page header ── */}
      <div className="row py-1">
        <p>
          <img className="px-1 icons-style" src="../assets/icons/003-clipboard.svg" alt="View" />
          <b className="px-1">Review mitigation </b>
          <img className="px-1 icons-style" src="../assets/icons/right-layer.svg" alt="View" />
          {urlparams.standard === 'CIS' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-cis.png" alt="View" draggable="false" />}
          {urlparams.standard === 'ISO-27001' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-iso-27001.png" alt="View" draggable="false" />}
          {urlparams.standard === 'NIST' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-nist.png" alt="View" draggable="false" />}
          {urlparams.standard === 'COBIT' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-cobit.png" alt="View" draggable="false" />}
          {urlparams.standard === 'TISAX' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-tisax.png" alt="View" draggable="false" />}
          {urlparams.standard === 'NIS 2.0' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-nis2.png" alt="View" draggable="false" />}
          {urlparams.standard === 'Ransomware' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-ransomware.png" alt="View" draggable="false" />}
          {urlparams.standard === 'Data Protection' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-data-protection.png" alt="View" draggable="false" />}
          {urlparams.standard === 'Supplier Risk' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-supplier-risk.png" alt="View" draggable="false" />}
          <span> {urlparams.standard} </span> - <span>{urlparams.assessmentid}</span>
        </p>
      </div>

      {loading && <LoadingOverlay />}

      <div>
        {assessmentsList.length > 0 && (
          <>
            {/* ── Export button ── */}
            <div className='export-excel'>
              <button className='approve-button' onClick={() => exportToExcel()}>
                Export to excel
              </button>
            </div>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={3}>Control name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage > 0
                    ? assessmentsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : assessmentsList
                  ).map((item) => (
                    <React.Fragment key={item.controlID}>

                      {/* ── Outer control row ── */}
                      <TableRow>
                        <TableCell>
                          <IconButton
                            className="expand-row"
                            size="small"
                            onClick={() => handleRowClick(item.controlID)}
                          >
                            {expandedRow === item.controlID ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{item.controlID + ' : ' + item.controlName}</TableCell>
                        <TableCell>{item.assessment_id}</TableCell>
                      </TableRow>

                      {/* ── Expanded subcontrols row ── */}
                      <TableRow>
                        <TableCell colSpan={3} sx={{ p: 0 }}>
                          <Collapse in={expandedRow === item.controlID} timeout="auto" unmountOnExit>
                            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: "6%" }}>Ref</TableCell>
                                  <TableCell sx={{ width: "18%" }}>Control Question</TableCell>
                                  <TableCell sx={{ width: "14%" }}>Answer Given</TableCell>
                                  <TableCell sx={{ width: "16%" }}>Mitigation Suggested</TableCell>
                                  {mitigationRejection && (
                                    <TableCell sx={{ width: "13%" }}>Reject Remarks</TableCell>
                                  )}
                                  {showResponse && (
                                    <TableCell sx={{ width: "18%" }}>Mitigation Response</TableCell>
                                  )}
                                  {showResponse && (
                                    <TableCell sx={{ width: "8%" }}>Evidence</TableCell>
                                  )}
                                  <TableCell align="center" sx={{ width: "10%" }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {item.subcontrols.map((subcontrol) => {
                                  const qType = subcontrol.question_type || "five_option";
                                  return (
                                    <TableRow key={subcontrol.subcontrolID}>

                                      {/* Ref */}
                                      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                                        {subcontrol.subcontrol}
                                      </TableCell>

                                      {/* Control Question */}
                                      <TableCell sx={{ verticalAlign: "top", wordBreak: "break-word" }}>
                                        <div className="subcontrols">{subcontrol.subcontrolName}</div>
                                      </TableCell>

                                      {/* Answer Given — QuestionRenderer read-only */}
                                      <TableCell sx={{ verticalAlign: "top" }}>
                                        <QuestionRenderer
                                          question_type={qType}
                                          answer_config={subcontrol.answer_config || null}
                                          value={subcontrol.answer_value || subcontrol.severity}
                                          readOnly={true}
                                        />
                                      </TableCell>

                                      {/* Mitigation Suggested */}
                                      <TableCell sx={{ verticalAlign: "top", wordBreak: "break-word" }}
                                        className="subcontrols">
                                        {subcontrol.mitigation}
                                      </TableCell>

                                      {/* Reject Remarks — conditional */}
                                      {mitigationRejection && (
                                        <TableCell sx={{ verticalAlign: "top", wordBreak: "break-word" }}>
                                          {subcontrol.comments}
                                        </TableCell>
                                      )}

                                      {/* Mitigation Response — conditional */}
                                      {showResponse && (
                                        <TableCell sx={{ verticalAlign: "top", wordBreak: "break-word" }}
                                          className="subcontrols">
                                          {subcontrol.mitigationresponse?.remarks !== undefined
                                            ? subcontrol.mitigationresponse.remarks
                                            : ''}
                                        </TableCell>
                                      )}

                                      {/* Evidence — conditional */}
                                      {showResponse && (
                                        <TableCell sx={{ verticalAlign: "top" }}>
                                          {subcontrol.mitigationresponse?.evidence !== undefined && (
                                            <a className="text-primary"
                                              href={subcontrol.mitigationresponse.evidence}
                                              target="_blank" rel="noreferrer">
                                              Download
                                            </a>
                                          )}
                                        </TableCell>
                                      )}

                                      {/* Actions — View Details */}
                                      <TableCell align="center" sx={{ verticalAlign: "top" }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<InfoOutlinedIcon />}
                                          onClick={() => openDetailDialog(subcontrol)}
                                          sx={{
                                            textTransform: "none",
                                            fontSize: "12px",
                                            whiteSpace: "nowrap",
                                            borderColor: "#1976d2",
                                            color: "#1976d2",
                                            "&:hover": { backgroundColor: "#e3f2fd" },
                                          }}
                                        >
                                          View Details
                                        </Button>
                                      </TableCell>

                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>

                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                component="div"
                count={assessmentsList.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </>
        )}

        {/* ── Action buttons ── */}
        {assessmentsList.length > 0 && (
          <div className='assessments-list'>
            <button className="go-back-button w-150" onClick={() => goBack()}>
              <ArrowLeftIcon /> Go back
            </button>
            {(urlparams.status === 'Mitigation Response Submitted' || urlparams.status === 'Review Mitigation Response') && (
              <>
                <button className="approve-button mx-4 w-150" onClick={() => setIsApprove(true)}>
                  Approve
                </button>
                <button className="reject-button w-150" onClick={() => setConfirmReject(true)}>
                  Reject
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Approve dialog ── */}
        {isApprove && (
          <Dialog onClose={() => setIsApprove(false)} open={isApprove}>
            <DialogTitle sx={{ m: 0, p: 2 }} />
            <DialogContent>
              <Typography gutterBottom>
                <div className='audit-trail-popuptext'>
                  <div className="popup-body">
                    <div className='txt-c'>
                      <CreditScore color="success" className='text-size' />
                    </div>
                    <div className='padd-l20'>
                      <div className='my-4 txt-c clr-b'>
                        <b>Do you want to approve mitigation?</b>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Button className="danger btn-card" variant="contained" onClick={() => setIsApprove(false)}>
                      No, I will review later
                    </Button>
                    <Button className="btn-card" variant="contained" onClick={() => handleApprove()}>
                      Yes, approve
                    </Button>
                  </div>
                </div>
              </Typography>
            </DialogContent>
          </Dialog>
        )}

        {/* ── Confirm reject dialog ── */}
        {confirmReject && (
          <Dialog onClose={() => setConfirmReject(false)} open={confirmReject}>
            <DialogTitle sx={{ m: 0, p: 2 }} />
            <DialogContent>
              <Typography gutterBottom>
                <div className='audit-trail-popuptext'>
                  <div className="popup-body">
                    <div className='txt-c'>
                      <img src="../assets/icons/alert.svg" className='w-40' draggable="false" alt='' />
                    </div>
                    <div className='padd-l20'>
                      <div className='my-4 txt-c clr-b'>
                        <b>Are you sure you want to reject the mitigation response?</b>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Button variant="contained" className="btn-card danger" onClick={() => setConfirmReject(false)}>
                      Cancel
                    </Button>
                    <Button variant="contained" className="btn-card" onClick={() => handleReject()}>
                      Yes
                    </Button>
                  </div>
                </div>
              </Typography>
            </DialogContent>
          </Dialog>
        )}

        {/* ── View Details dialog ── */}
        {detailDialog && selectedSubcontrol && (
          <Dialog
            open={detailDialog}
            onClose={closeDetailDialog}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "10px" } }}
          >
            <DialogTitle sx={{ m: 0, p: 2, backgroundColor: "#1a2b5e", color: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                <span style={{ fontSize: "15px", fontWeight: 600 }}>
                  {selectedSubcontrol.subcontrol} — Question Details
                </span>
              </div>
              <IconButton
                aria-label="close"
                onClick={closeDetailDialog}
                sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>

              {/* Control Question */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                  Control Question
                </div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#1a2b5e" }}>
                  {selectedSubcontrol.subcontrolName}
                </div>
              </div>

              <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

              {/* Answer Given */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Answer Given
                </div>
                <QuestionRenderer
                  question_type={selectedSubcontrol.question_type || "five_option"}
                  answer_config={selectedSubcontrol.answer_config || null}
                  value={selectedSubcontrol.answer_value || selectedSubcontrol.severity}
                  readOnly={true}
                />
              </div>

              <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

              {/* Rationale */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Rationale for Rating
                </div>
                <div style={{
                  background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px",
                  padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                  color: selectedSubcontrol.rational ? "#333" : "#aaa",
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {selectedSubcontrol.rational || "No rationale provided"}
                </div>
              </div>

              <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

              {/* Mitigation Suggested */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Mitigation Suggested
                </div>
                <div style={{
                  background: "#f0f7ff", border: "1px solid #b3d4f5", borderRadius: "6px",
                  padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                  color: selectedSubcontrol.mitigation ? "#1a2b5e" : "#aaa",
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {selectedSubcontrol.mitigation || "No mitigation suggestion provided"}
                </div>
              </div>

              {/* Reject Remarks — only if present */}
              {mitigationRejection && selectedSubcontrol.comments && (
                <>
                  <hr style={{ margin: "12px 0", borderColor: "#eee" }} />
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontSize: "11px", color: "#B60000", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Reject Remarks
                    </div>
                    <div style={{
                      background: "#fff5f5", border: "1px solid #ffcccc", borderRadius: "6px",
                      padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                      color: "#B60000", whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                      {selectedSubcontrol.comments}
                    </div>
                  </div>
                </>
              )}

              {/* Mitigation Response — only when response columns are visible */}
              {showResponse && (
                <>
                  <hr style={{ margin: "12px 0", borderColor: "#eee" }} />
                  <div style={{ marginBottom: selectedSubcontrol.mitigationresponse?.evidence ? "16px" : 0 }}>
                    <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Mitigation Response
                    </div>
                    <div style={{
                      background: "#f0fff4", border: "1px solid #b3e6c8", borderRadius: "6px",
                      padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                      color: selectedSubcontrol.mitigationresponse?.remarks ? "#1a5e2b" : "#aaa",
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                      {selectedSubcontrol.mitigationresponse?.remarks || "No Mitigation Response provided"}
                    </div>
                  </div>
                </>
              )}

              {/* Evidence — only when present */}
              {showResponse && selectedSubcontrol.mitigationresponse?.evidence && (
                <>
                  <hr style={{ margin: "12px 0", borderColor: "#eee" }} />
                  <div>
                    <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Evidence
                    </div>
                    <a
                      className="text-primary"
                      href={selectedSubcontrol.mitigationresponse.evidence}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: "13px" }}
                    >
                      Download Evidence
                    </a>
                  </div>
                </>
              )}

            </DialogContent>
          </Dialog>
        )}

      </div>
    </div>
  );
};

export default ReviewMitigationPage;